import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  Grid,
  InputAdornment,
  TableRow,
  TableCell,
  Button,
  Autocomplete,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import React, { useEffect, useState } from 'react';
import PageHeader from '../../../components/PageHeader';
import Table from '../../../components/Table';
import { Company } from '../../../schema/company';
import { Person, PersonType } from '../../../schema/person';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import MultiSelect from '../../../components/MultiSelect';
import { TableResponse } from '../../../schema/helper';
import { api } from '../../../api';

const tableHead = [
  'เลขบัตรประชาชน',
  'ชื่อ นามสกุล',
  'ประเภท',
  'บริษัท',
  'ผู้คุมงาน',
  'พื้นที่ทั่วไป',
  'พื้นที่ควบคุม',
  'Actions'
];

const PersonPage = () => {
  const navigate = useNavigate();
  const [searchParamsInput] = useSearchParams();
  const [filterID, setFilterID] = useState<string>(
    (searchParamsInput.get('cid') ? searchParamsInput.get('cid') : '') + ''
  );
  const [filterName, setFilterName] = useState<string>('');
  const [filterCompanyIDList, setFilterCompanyIDList] = useState<string[] | number[]>([]);
  const [filterPersonTypeIDList, setFilterPersonTypeIDList] = useState<string[] | number[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [searchCompanyKeyword, setSearchCompanyKeyword] = useState<string>('');
  const [personTypes, setPersonTypes] = useState<PersonType[]>([]);
  const [data, setData] = useState<TableResponse<Person> | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);

  // setFilterID(searchParams.get('cid') + "")

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleExportCSVFile = () => {
    if (isExportLoading) {
      return;
    }
    const searchParams = new URLSearchParams();

    if (filterID) {
      searchParams.set('personal_id', `${filterID}`);
    }
    if (filterName) {
      searchParams.set('name', `${filterName}`);
    }
    if (filterCompanyIDList.length) {
      filterCompanyIDList.forEach((company_id) => {
        searchParams.append('company_id', `${company_id}`);
      });
    }
    if (filterPersonTypeIDList.length) {
      filterPersonTypeIDList.forEach((person_type_id) => {
        searchParams.append('person_type_id', `${person_type_id}`);
      });
    }
    setIsExportLoading(true);
    api
      .get(`/person/export`, { params: searchParams, responseType: 'blob' })
      .then((response) => {
        const today = new Date();
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          'person_export_data_' + today.toLocaleDateString('en-US') + '.xlsx'
        );
        document.body.appendChild(link);
        link.click();
      })
      .finally(() => setIsExportLoading(false));
  };

  useEffect(() => {
    setIsLoading(true);
    const timeOutId = setTimeout(() => {
      const searchParams = new URLSearchParams();
      if (searchParamsInput.get('cid')) {
        searchParams.set('personnal_id', `${searchParamsInput.get('cid')}`);
        // setFilterID(searchParamsInput.get('cid') + "")
      }
      if (filterID) {
        searchParams.set('personnal_id', `${filterID}`);
      }
      if (filterName) {
        searchParams.set('name', `${filterName}`);
      }
      if (filterCompanyIDList.length) {
        filterCompanyIDList.forEach((company_id) => {
          searchParams.append('company_id', `${company_id}`);
        });
      }
      if (filterPersonTypeIDList.length) {
        filterPersonTypeIDList.forEach((person_type_id) => {
          searchParams.append('person_type_id', `${person_type_id}`);
        });
      }
      // else {
      //   searchParams.append('is_staff', `${false}`);
      // }
      if (filterStatus) {
        if (filterStatus == 'active') {
          searchParams.set('is_active', `${true}`);
        } else if (filterStatus == 'inactive') {
          searchParams.set('is_active', `${false}`);
        }
      }

      searchParams.set('offset', `${page * rowsPerPage}`);
      searchParams.set('limit', `${rowsPerPage}`);
      api
        .get<TableResponse<Person>>(`/person/table`, { params: searchParams })
        .then((res) => {
          const { data } = res;
          setData(data);
        })
        .finally(() => setIsLoading(false));
    }, 500);
    return () => clearTimeout(timeOutId);
  }, [
    filterID,
    filterStatus,
    filterName,
    filterCompanyIDList,
    filterPersonTypeIDList,
    page,
    rowsPerPage
  ]);

  useEffect(() => {
    api
      .get<Company[]>(`/company`, {
        params: {
          limit: 25
        }
      })
      .then((res) => {
        const { data } = res;
        setCompanies(data);
      });
    api.get<PersonType[]>(`/person-type`).then((res) => {
      const { data } = res;
      setPersonTypes(data);
    });
  }, []);

  useEffect(() => {
    if (searchCompanyKeyword) {
      const timeOut = setTimeout(() => {
        const searchParams = new URLSearchParams();
        searchParams.set('company_name', searchCompanyKeyword);
        searchParams.set('limit', '25');

        api.get<Company[]>(`/company`, { params: searchParams }).then((res) => {
          const { data } = res;
          setCompanies(data);
        });
      }, 500);
      return () => clearTimeout(timeOut);
    }
  }, [searchCompanyKeyword]);

  return (
    <Container>
      <PageHeader text="รายงานบุคคลในระบบ" />

      <Grid container spacing={2} marginTop={1}>
        <Grid item md={3}>
          <TextField
            id="search-text-label"
            label="เลขบัตรประชาชน"
            variant="outlined"
            placeholder="เลขบัตรประชาชน"
            value={filterID}
            onChange={(e) => {
              setFilterID(e.target.value);
              searchParamsInput.delete('cid');
            }}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            autoComplete="off"
          />
        </Grid>
        <Grid item md={2}>
          <TextField
            id="search-text-label"
            label="ค้นหาข้อมูล"
            variant="outlined"
            placeholder="ค้นหา ชื่อ-นามสกุล"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            autoComplete="off"
          />
        </Grid>
        <Grid item md={2}>
          <Autocomplete
            multiple
            limitTags={2}
            disablePortal
            options={companies}
            getOptionLabel={(option) => option.company_name}
            renderInput={(params) => <TextField {...params} label="บริษัท" />}
            onChange={(_, value) => setFilterCompanyIDList(value.map((data) => data.id))}
            onInputChange={(_, newInputValue) => {
              setSearchCompanyKeyword(newInputValue);
            }}
            fullWidth
          />
        </Grid>
        <Grid item md={2}>
          <MultiSelect
            options={personTypes.map((data) => ({ id: data.id.toString(), name: data.type_name }))}
            onChange={(data) => setFilterPersonTypeIDList(data)}
            label="ประเภทบุคคล"
          />
        </Grid>
        <Grid item md={2}>
          <FormControl fullWidth>
            <InputLabel id="select-status-label">สถานะ</InputLabel>
            <Select
              labelId="select-status-label"
              value={filterStatus}
              label="สถานะ"
              name="status"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="">ทั้งหมด</MenuItem>
              <MenuItem value="active">อนุญาตแลกบัตร</MenuItem>
              <MenuItem value="inactive">ไม่อนุญาตแลกบัตร</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item md={1} sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button variant="outlined" color="primary" onClick={handleExportCSVFile} disabled={isExportLoading}>
            Export
          </Button>
        </Grid>
      </Grid>
      <Box sx={{ mt: 2 }}>
        <Table
          heads={tableHead}
          count={data?.total ? data.total : 0}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          isLoading={isLoading}
        >
          {(!data || (data.total == 0 && searchParamsInput.get('cid'))) && (
            <>
              <Typography variant="subtitle2" mt={3} ml={3} sx={{ color: 'red' }} gutterBottom>
                ไม่พบข้อมูลหมายเลขบัตรประชาชน `{searchParamsInput.get('cid')}`
              </Typography>
            </>
          )}
          {data?.data.map((data, index) => (
            <TableRow key={index}>
              <TableCell align="center">{data.personnal_id}</TableCell>
              <TableCell align="center">{data.full_name}</TableCell>
              <TableCell align="center">{data.person_type.type_name}</TableCell>
              <TableCell align="center">{data.company.company_name}</TableCell>
              <TableCell align="center">{data.supervisor?.full_name}</TableCell>
              <TableCell align="center">
                <Typography variant="body2" color={data.is_active ? 'success.main' : 'error'}>
                  {data.is_active
                    ? data.is_temp_active
                      ? 'อนุญาตโดยเจ้าหน้าที่'
                      : 'อนุญาตโดยระบบ'
                    : 'ไม่อนุญาตแลกบัตร'}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography
                  variant="body2"
                  color={data.is_control_area_active ? 'success.main' : 'error'}
                >
                  {data.is_control_area_active
                    ? data.is_control_area_temp_active
                      ? 'อนุญาตโดยเจ้าหน้าที่'
                      : 'อนุญาตโดยระบบ'
                    : 'ไม่อนุญาตแลกบัตร'}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Grid container spacing={1} alignItems="center" justifyContent="center">
                  <Grid item>
                    <Button
                      component={Link}
                      to={`/person/${data.id}`}
                      variant="contained"
                      color="success"
                    >
                      ข้อมูล
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      component={Link}
                      to={`/person/${data.id}/edit`}
                      variant="contained"
                      color="warning"
                    >
                      แก้ไข
                    </Button>
                  </Grid>
                </Grid>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      </Box>
    </Container>
  );
};

export default PersonPage;

const Container = styled('div')`
  padding: 24px;
`;
